import React, { useEffect, useState } from "react";
import { Input, Tooltip, Select, Button, Slider, Typography, Spin } from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
// import CustomSwitch from "./switch/CustomSwitch";
import { FinetuningService } from "../../services/FinetuningService";
import { DatasetService } from "../../services/DatasetService";
import { usePermissions } from "../../PermissionsProvider";
import { FinetuneDataset } from "../../model_gen/finetuning";

const { Text } = Typography;

interface FinetuneSettingsProps {
  exportPrompt: string;
  setExportPrompt: (value: string) => void;
  fourbit: boolean;
  setFourbit: (value: boolean) => void;
  isExporting: boolean;
  baseModel: string;
  setBaseModel: (value: string) => void;
  promptDeletions: string[];
  setPromptDeletions: (value: string[]) => void | null;
  warmupSteps: number;
  setWarmupSteps: (value: number) => void;
  learningRate: number;
  setLearningRate: (value: number) => void;
  trainSplit?: number;
  setTrainSplit?: (value: number) => void;
  hideAdvancedSettings?: boolean;
  showOnlyAdvancedSettings?: boolean;
  intentGroupId?: number | null;
  selectedSubDataset?: FinetuneDataset | null;
  setSelectedSubDataset?: (dataset: FinetuneDataset | null) => void;
  displayName?: string;
  setDisplayName?: (value: string) => void;
}

// Helper component for field label (works for both required and optional fields)
const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  tooltip?: string;
}> = ({ label, required = false, tooltip }) => (
  <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
    <Text strong>{label}</Text>
    {required && (
      <Text type="danger" style={{ marginLeft: 4 }}>
        *
      </Text>
    )}
    {tooltip && (
      <Tooltip title={tooltip}>
        <InfoCircleOutlined
          style={{ marginLeft: 8, color: "rgba(0, 0, 0, 0.45)" }}
        />
      </Tooltip>
    )}
  </div>
);

const FinetuneSettings: React.FC<FinetuneSettingsProps> = ({
  exportPrompt,
  setExportPrompt,
  // fourbit,
  // setFourbit,
  isExporting,
  baseModel,
  setBaseModel,
  promptDeletions,
  setPromptDeletions,
  warmupSteps,
  setWarmupSteps,
  learningRate,
  setLearningRate,
  trainSplit,
  setTrainSplit,
  hideAdvancedSettings = false,
  showOnlyAdvancedSettings = false,
  intentGroupId = null,
  selectedSubDataset = null,
  setSelectedSubDataset,
}) => {
  const { apiKey } = usePermissions();
  const [baseModels, setBaseModels] = useState<string[]>([]);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState<boolean>(false);

  // Fetch base models
  useEffect(() => {
    if (!showOnlyAdvancedSettings) {
      FinetuningService.getBaseModels(() => {}, apiKey).subscribe(
        (models: string[]) => {
          setBaseModels(models);
          setBaseModel(models[0]);
        },
        (error: any) => {
          console.error("Failed to fetch base models:", error);
        }
      );
    }
  }, [apiKey, showOnlyAdvancedSettings]);

  // Fetch datasets for the intent group
  useEffect(() => {
    if (intentGroupId) {
      setLoadingDatasets(true);
      DatasetService.getDatasetsByIntentGroup(
        intentGroupId,
        0,
        100,
        setLoadingDatasets,
        apiKey
      ).subscribe(
        (response: any) => {
          if (response && response.items) {
            setDatasets(response.items);
          } else {
            setDatasets([]);
          }
        },
        (error: any) => {
          console.error("Failed to fetch datasets:", error);
          setDatasets([]);
          setLoadingDatasets(false);
        }
      );
    }
  }, [intentGroupId, apiKey]);

  // Helper to render dropdown with loading spinner
  const renderDropdownWithSpinner = (loading: boolean, isEmpty: boolean) => {
    if (loading) {
      return <Spin size="small" />;
    }
    if (isEmpty) {
      return "No datasets found";
    }
    return null;
  };

  // Helper to format dataset label
  const formatDatasetLabel = (dataset: FinetuneDataset) => {
    let sourceName = "";
    if (dataset.application_name && dataset.intent_name) {
      sourceName = `${dataset.application_name} / ${dataset.intent_name}`;
    } else if (dataset.application_name) {
      sourceName = dataset.application_name;
    } else if (dataset.intent_name) {
      sourceName = dataset.intent_name;
    } else if (dataset.intent_group_name) {
      sourceName = dataset.intent_group_name;
    } else {
      sourceName = "Unknown source";
    }

    return `${dataset.name} - ${sourceName}`;
  };

  // Handle dataset selection change
  const handleDatasetChange = (value: number | string | undefined) => {
    if (setSelectedSubDataset) {
      if (value) {
        const valueStr = value.toString();
        const selectedDataset = datasets.find(
          (dataset) => dataset.id?.toString() === valueStr
        );
        setSelectedSubDataset(selectedDataset || null);
      } else {
        setSelectedSubDataset(null);
      }
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <>
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                placeholder="Select a sub dataset"
                value={selectedSubDataset?.id?.toString()}
                onChange={handleDatasetChange}
                style={{ width: "100%", marginBottom: 24 }}
                disabled={isExporting}
                loading={loadingDatasets}
                showSearch
                optionFilterProp="children"
                allowClear
                notFoundContent={renderDropdownWithSpinner(
                  loadingDatasets,
                  datasets.length === 0
                )}>
                {datasets.map((dataset) => (
                  <Select.Option
                    key={dataset.id}
                    value={dataset.id?.toString()}>
                    {formatDatasetLabel(dataset)}
                  </Select.Option>
                ))}
              </Select>
            </>
          )}

          {/* Commented out 4-bit quantization option
          <FieldLabel label="4-bit Quantization" tooltip="Use 4-bit quantization for finetuning" />
          <div
            className="configure-menu-list-item-wrap"
            style={{ marginBottom: 24 }}>
            <CustomSwitch
              className="custom-switch"
              checked={fourbit}
              onChange={(checked) => setFourbit(checked)}
              disabled={isExporting}
            />
          </div>
          */}

          <FieldLabel label="Prompt Deletions" />
          <div style={{ marginBottom: 24 }}>
            <Button
              onClick={() => setPromptDeletions([...promptDeletions, ""])}
              icon={<PlusOutlined />}
              style={{ marginBottom: 8 }}
              disabled={isExporting || !setPromptDeletions}>
              Add Deletion
            </Button>

            {promptDeletions &&
              promptDeletions.map((deletion, index) => (
                <div key={index} style={{ display: "flex", marginBottom: 8 }}>
                  <Input
                    value={deletion}
                    onChange={(e) => {
                      if (setPromptDeletions) {
                        const newDeletions = [...promptDeletions];
                        newDeletions[index] = e.target.value;
                        setPromptDeletions(newDeletions);
                      }
                    }}
                    disabled={isExporting || !setPromptDeletions}
                    style={{ marginRight: 8 }}
                  />
                  <Button
                    icon={<MinusCircleOutlined />}
                    onClick={() => {
                      if (setPromptDeletions) {
                        setPromptDeletions(
                          promptDeletions.filter((_, i) => i !== index)
                        );
                      }
                    }}
                    disabled={isExporting || !setPromptDeletions}
                  />
                </div>
              ))}
          </div>

          <FieldLabel
            label="Training Parameters"
            tooltip="Configure model training parameters"
          />
          <div style={{ marginBottom: 24 }}>
            <div
              style={{ display: "flex", gap: "16px", flexDirection: "column" }}>
              <div>
                <Text style={{ display: "block", marginBottom: 4 }}>
                  Warmup Steps
                </Text>
                <Input
                  type="number"
                  value={warmupSteps}
                  onChange={(e) => setWarmupSteps(parseInt(e.target.value))}
                  disabled={isExporting}
                  min={0}
                />
              </div>
              <div>
                <Text style={{ display: "block", marginBottom: 4 }}>
                  Learning Rate
                </Text>
                <Input
                  type="number"
                  value={learningRate}
                  onChange={(e) => setLearningRate(parseFloat(e.target.value))}
                  disabled={isExporting}
                  min={0}
                  step={0.0001}
                />
              </div>
            </div>
          </div>

          {setTrainSplit && (
            <>
              <FieldLabel
                label="Train Split"
                tooltip="Percentage of data to use for training (remainder used for testing)"
              />
              <div
                className="custom_slider_wrapper"
                style={{ marginBottom: 24 }}>
                <div style={{ width: "100%", padding: "8px" }}>
                  <Slider
                    min={0}
                    max={1}
                    className="cus-slider-wrap"
                    tooltip={{
                      formatter: (value) => `${(value * 100).toFixed(0)}%`,
                      placement: "top",
                    }}
                    value={trainSplit}
                    step={0.01}
                    onChange={(value: number) => setTrainSplit(value)}
                    disabled={isExporting}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!showOnlyAdvancedSettings && (
        <>
          <FieldLabel
            label="Prompt"
            tooltip="Optional prompt for the finetune"
          />
          <Input.TextArea
            placeholder="Enter prompt (optional)"
            value={exportPrompt}
            onChange={(e) => setExportPrompt(e.target.value)}
            rows={4}
            disabled={isExporting}
            style={{ marginBottom: 24 }}
          />

          <FieldLabel label="Base Model" required />
          <Select
            style={{ width: "100%", marginBottom: 24 }}
            value={baseModel}
            onChange={setBaseModel}
            disabled={isExporting}
            options={baseModels.map((model) => ({
              value: model,
              label: model,
            }))}
            placeholder="Select base model"
            status={!baseModel.trim() ? "error" : ""}
          />
        </>
      )}

      {!hideAdvancedSettings && !showOnlyAdvancedSettings && (
        <>
          {/* Only show Sub Dataset if intentGroupId is not null */}
          {intentGroupId && (
            <>
              <FieldLabel
                label="Sub Dataset"
                tooltip="Optional. If set, this will target training with a second finetune. Leave empty for standard finetuning."
              />
              <Select
                placeholder="Select a sub dataset"
                value={selectedSubDataset?.id?.toString()}
                onChange={handleDatasetChange}
                style={{ width: "100%", marginBottom: 24 }}
                disabled={isExporting}
                loading={loadingDatasets}
                showSearch
                optionFilterProp="children"
                allowClear
                notFoundContent={renderDropdownWithSpinner(
                  loadingDatasets,
                  datasets.length === 0
                )}>
                {datasets.map((dataset) => (
                  <Select.Option
                    key={dataset.id}
                    value={dataset.id?.toString()}>
                    {formatDatasetLabel(dataset)}
                  </Select.Option>
                ))}
              </Select>
            </>
          )}

          {/* Commented out 4-bit quantization option
          <FieldLabel label="4-bit Quantization" tooltip="Use 4-bit quantization for finetuning" />
          <div
            className="configure-menu-list-item-wrap"
            style={{ marginBottom: 24 }}>
            <CustomSwitch
              className="custom-switch"
              checked={fourbit}
              onChange={(checked) => setFourbit(checked)}
              disabled={isExporting}
            />
          </div>
          */}

          <FieldLabel label="Prompt Deletions" />
          <div style={{ marginBottom: 24 }}>
            <Button
              onClick={() => setPromptDeletions([...promptDeletions, ""])}
              icon={<PlusOutlined />}
              style={{ marginBottom: 8 }}
              disabled={isExporting || !setPromptDeletions}>
              Add Deletion
            </Button>

            {promptDeletions &&
              promptDeletions.map((deletion, index) => (
                <div key={index} style={{ display: "flex", marginBottom: 8 }}>
                  <Input
                    value={deletion}
                    onChange={(e) => {
                      if (setPromptDeletions) {
                        const newDeletions = [...promptDeletions];
                        newDeletions[index] = e.target.value;
                        setPromptDeletions(newDeletions);
                      }
                    }}
                    disabled={isExporting || !setPromptDeletions}
                    style={{ marginRight: 8 }}
                  />
                  <Button
                    icon={<MinusCircleOutlined />}
                    onClick={() => {
                      if (setPromptDeletions) {
                        setPromptDeletions(
                          promptDeletions.filter((_, i) => i !== index)
                        );
                      }
                    }}
                    disabled={isExporting || !setPromptDeletions}
                  />
                </div>
              ))}
          </div>

          {setTrainSplit && (
            <>
              <FieldLabel
                label="Train Split"
                tooltip="Percentage of data to use for training (remainder used for testing)"
              />
              <div
                className="custom_slider_wrapper"
                style={{ marginBottom: 24 }}>
                <div style={{ width: "100%", padding: "8px" }}>
                  <Slider
                    min={0}
                    max={1}
                    className="cus-slider-wrap"
                    tooltip={{
                      formatter: (value) => `${(value * 100).toFixed(0)}%`,
                      placement: "top",
                    }}
                    value={trainSplit}
                    step={0.01}
                    onChange={(value: number) => setTrainSplit(value)}
                    disabled={isExporting}
                  />
                </div>
              </div>
            </>
          )}

          <FieldLabel
            label="Training Parameters"
            tooltip="Configure model training parameters"
          />
          <div style={{ marginBottom: 24 }}>
            <div
              style={{ display: "flex", gap: "16px", flexDirection: "column" }}>
              <div>
                <Text style={{ display: "block", marginBottom: 4 }}>
                  Warmup Steps
                </Text>
                <Input
                  type="number"
                  value={warmupSteps}
                  onChange={(e) => setWarmupSteps(parseInt(e.target.value))}
                  disabled={isExporting}
                  min={0}
                />
              </div>
              <div>
                <Text style={{ display: "block", marginBottom: 4 }}>
                  Learning Rate
                </Text>
                <Input
                  type="number"
                  value={learningRate}
                  onChange={(e) => setLearningRate(parseFloat(e.target.value))}
                  disabled={isExporting}
                  min={0}
                  step={0.0001}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinetuneSettings;
